import React, { useMemo, useState } from "react";
import { Box, Button, Sheet, Tooltip, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import { AddIcon } from "static/image/common/IconAssets";
import {
  REPEAT_SET_NUMBER,
  TASK_TYPE_LABELS,
  TOOL_SET_NUMBER,
  type TaskEditorState,
} from "./utils/taskEditorUtils";
import {
  buildTaskEditorListSections,
  getTaskMetaIssue,
} from "./utils/taskEditorListUtils";
import {
  useTaskEditorListDrag,
  type ListDropTarget,
} from "./utils/useTaskEditorListDrag";
import { taskEditorScrollbarSx } from "./utils/taskEditorScrollbarSx";
import { ProgramVersionModal } from "./ProgramVersionModal";
import { UpArrowSvg } from "../../static/image/common/SvgAssets";

interface TaskListPanelProps {
  tasks: TaskEditorState[];
  activeTaskId?: string;
  // 백그라운드 포함 Agent 스트리밍 중인 활동지 id — 제목 좌측 진행 인디케이터 표시용.
  streamingTaskIds?: string[];
  // 하단 footer 의 접기 토글. 접힌 상태의 펼치기 버튼은 페이지가 floating 으로 렌더.
  onToggleCollapse: () => void;
  onSelectTask: (taskId: string) => void;
  onCreateTask: () => void;
  onEditMeta: (taskId: string) => void;
  // 드래그 1회의 결과. targetSetNumber=9999 는 미분류, beforeTaskId=null 은 섹션 끝.
  onReorder: (
    draggedTaskId: string,
    targetSetNumber: number,
    beforeTaskId: string | null
  ) => void;
}

const KEY_BADGE_COLOR = "#F59E0B";
// 프로그램 버전 — 아직 단일 버전. 배포 기능이 붙으면 서버 값으로 대체 예정.
const PROGRAM_VERSION = "v1.0.0";
const DROP_LINE_COLOR = theme.vars.palette.primary.solid;

// Agent 응답 스트리밍 중인 활동지의 제목 좌측에 띄우는 진행 인디케이터.
// "생각을 다듬는 중" — 세 점이 순차로 일렁이는(opacity wave) 잔잔한 애니메이션.
function StreamingIndicator() {
  const dotSx = (delay: string) => ({
    width: "3px",
    height: "3px",
    borderRadius: "50%",
    backgroundColor: theme.vars.palette.Content.Highlight,
    "@keyframes taskThinking": {
      "0%, 80%, 100%": { opacity: 0.25 },
      "40%": { opacity: 1 },
    },
    animation: "taskThinking 1.2s ease-in-out infinite",
    animationDelay: delay,
  });
  return (
    <Box
      aria-label="Agent 응답 생성 중"
      title="Agent 응답 생성 중"
      sx={{ display: "flex", alignItems: "center", gap: "2px" }}
    >
      <Box sx={dotSx("0s")} />
      <Box sx={dotSx("0.16s")} />
      <Box sx={dotSx("0.32s")} />
    </Box>
  );
}

// 패널 접기/펼치기 토글 (Claude Code 사이드바 토글과 동일한 panel 아이콘).
// 접힌 상태의 floating 펼치기 버튼으로도 쓰도록 export.
export function CollapseToggleButton({
  collapsed,
  onClick,
}: {
  collapsed: boolean;
  onClick: () => void;
}) {
  const label = collapsed ? "활동지 목록 펼치기" : "활동지 목록 접기";
  return (
    <Box
      role="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: "4px",
        borderRadius: "6px",
        cursor: "pointer",
        color: theme.vars.palette.Content.Weak,
        "&:hover": {
          color: theme.vars.palette.Content.Strong,
          backgroundColor: theme.vars.palette.Background.Level1,
        },
      }}
    >
      <span
        className="material-symbols-outlined"
        style={{
          fontSize: "18px",
          // 아이콘 폰트 로드 전 ligature 원문이 폭을 차지하지 않게 글리프
          // 크기로 박스 고정 (more_vert 와 동일한 처리).
          width: "18px",
          height: "18px",
          overflow: "hidden",
          display: "inline-block",
        }}
      >
        {collapsed ? "left_panel_open" : "left_panel_close"}
      </span>
    </Box>
  );
}

export default function TaskListPanel({
  tasks,
  activeTaskId,
  streamingTaskIds,
  onToggleCollapse,
  onSelectTask,
  onCreateTask,
  onEditMeta,
  onReorder,
}: TaskListPanelProps) {
  const sections = useMemo(() => buildTaskEditorListSections(tasks), [tasks]);
  // 필수 메타 누락/참조 깨짐 판별용 (getTaskMetaIssue 의 참조 존재 검사).
  const tasksByTaskId = useMemo(() => {
    const map: Record<string, TaskEditorState> = {};
    tasks.forEach((task) => {
      map[task.task_id] = task;
    });
    return map;
  }, [tasks]);
  const streamingSet = useMemo(
    () => new Set(streamingTaskIds ?? []),
    [streamingTaskIds]
  );
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set()
  );
  const [versionModalOpen, setVersionModalOpen] = useState(false);

  const {
    dragState,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useTaskEditorListDrag(onReorder);
  const { draggingTaskId, dropTarget } = dragState;

  const toggleSection = (sectionKey: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  // 행 위 포인터 위치(상/하단)로 "앞/뒤 삽입" 결정.
  const buildRowTarget = (
    event: React.DragEvent<HTMLElement>,
    setNumber: number,
    taskId: string,
    nextTaskId: string | null
  ): ListDropTarget => {
    const rect = event.currentTarget.getBoundingClientRect();
    const isAfter = event.clientY > rect.top + rect.height / 2;
    return { setNumber, beforeTaskId: isAfter ? nextTaskId : taskId };
  };

  const isDropLineActive = (setNumber: number, beforeTaskId: string | null) =>
    dropTarget !== null &&
    dropTarget.setNumber === setNumber &&
    dropTarget.beforeTaskId === beforeTaskId;

  return (
    <Sheet
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderTop: 0,
        borderBottom: 0,
        borderLeft: 0,
        borderRadius: 0,
        backgroundColor: theme.vars.palette.Surface.Forward,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: "8px",
          px: "12px",
          py: "8px",
          overflow: "hidden",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        <Typography
          level="title-sm"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: "13px",
            fontWeight: 700,
            color: theme.vars.palette.Content.Strong,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          활동지 목록
        </Typography>
        <Button
          size="sm"
          variant="Plain"
          startDecorator={<AddIcon size={12} />}
          onClick={onCreateTask}
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            minHeight: "28px",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          새 활동지
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pt: "4px",
          pb: "48px",
          ...taskEditorScrollbarSx,
        }}
      >
        {sections.length === 0 && (
          <Box sx={{ p: "16px", textAlign: "center" }}>
            <Typography
              level="body-xs"
              sx={{ color: theme.vars.palette.Content.Weak }}
            >
              활동지 없음
            </Typography>
          </Box>
        )}

        {sections.map((section) => {
          const isCollapsed = collapsedSections.has(section.key);
          const firstTaskId = section.tasks[0]?.task_id ?? null;
          // 툴/반복 섹션 — 진입/이탈 드롭 모두 유형 전환. 페이지가 해당 유형이
          // 미리 선택된 메타 모달을 연다 (저장 = 전환 확정, 취소 = 원위치).
          // 섹션 내 위치는 의미 없으므로 드롭 타깃은 항상 섹션 맨 위로 고정.
          const isTypeSection =
            section.set_number === TOOL_SET_NUMBER ||
            section.set_number === REPEAT_SET_NUMBER;
          const sectionTopTarget = {
            setNumber: section.set_number,
            beforeTaskId: firstTaskId,
          };
          return (
            <Box key={section.key} sx={{ mb: "2px" }}>
              {/* 섹션 헤더 — 드롭 시 해당 세트의 맨 위로 */}
              <Box
                onClick={() => toggleSection(section.key)}
                onDragOver={(event) => handleDragOver(event, sectionTopTarget)}
                onDrop={(event) => handleDrop(event, sectionTopTarget)}
                sx={{
                  px: "8px",
                  py: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  userSelect: "none",
                  "&:hover": {
                    backgroundColor: theme.vars.palette.Background.Level1,
                  },
                }}
              >
                <Box
                  sx={{
                    ...(isCollapsed && {
                      transform: "rotate(-90deg)",
                      transformOrigin: "center",
                    }),
                    transition: "transform 0.2s",
                  }}
                >
                  <UpArrowSvg
                    color={theme.vars.palette.Content.Weak}
                    size={16}
                  />
                </Box>
                <Typography
                  level="body-xs"
                  sx={{
                    fontWeight: 700,
                    color: theme.vars.palette.Content.Weak,
                    fontSize: "11px",
                    letterSpacing: "0.02em",
                  }}
                >
                  {section.label}
                </Typography>
              </Box>

              {!isCollapsed &&
                section.tasks.map((task, taskIndex) => {
                  const isActive = activeTaskId === task.task_id;
                  const isStreamingTask = streamingSet.has(task.task_id);
                  const isBeingDragged = draggingTaskId === task.task_id;
                  const nextTaskId =
                    section.tasks[taskIndex + 1]?.task_id ?? null;
                  const isLast = taskIndex === section.tasks.length - 1;
                  const metaIssue = getTaskMetaIssue(task, tasksByTaskId);
                  const showTopLine = isDropLineActive(
                    section.set_number,
                    task.task_id
                  );
                  const showBottomLine =
                    isLast && isDropLineActive(section.set_number, null);

                  return (
                    <Box
                      key={task.task_id}
                      draggable
                      onDragStart={(event) =>
                        handleDragStart(event, task.task_id)
                      }
                      onDragEnd={handleDragEnd}
                      onDragOver={(event) =>
                        handleDragOver(
                          event,
                          isTypeSection
                            ? sectionTopTarget
                            : buildRowTarget(
                                event,
                                section.set_number,
                                task.task_id,
                                nextTaskId
                              )
                        )
                      }
                      onDrop={(event) =>
                        handleDrop(
                          event,
                          isTypeSection
                            ? sectionTopTarget
                            : buildRowTarget(
                                event,
                                section.set_number,
                                task.task_id,
                                nextTaskId
                              )
                        )
                      }
                      onClick={() => onSelectTask(task.task_id)}
                      sx={{
                        position: "relative",
                        // 제목 좌측 라인을 섹션 라벨("세트")의 텍스트 시작점과 정렬한다.
                        // 라벨 x = header px(8) + chevron(16) + gap(4) = 28px.
                        // 제목 x = border(2) + pl + slot(18) + gap(6) = 26 + pl → pl 2px.
                        pl: "2px",
                        pr: "4px",
                        py: "4px",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                        opacity: isBeingDragged ? 0.4 : 1,
                        borderLeft: `2px solid ${
                          isActive
                            ? theme.vars.palette.Content.Strong
                            : "transparent"
                        }`,
                        backgroundColor: isActive
                          ? theme.vars.palette.Background.Level2
                          : "transparent",
                        "& .meta-edit-btn": {
                          visibility: "hidden",
                        },
                        "&:hover": {
                          backgroundColor: isActive
                            ? theme.vars.palette.Background.Level2
                            : theme.vars.palette.Background.Level1,
                        },
                        "&:hover .meta-edit-btn": {
                          visibility: "visible",
                        },
                      }}
                    >
                      {/* 드롭 인디케이터 (드래그 중에만) */}
                      {showTopLine && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "-1px",
                            left: "8px",
                            right: "4px",
                            height: "2px",
                            backgroundColor: DROP_LINE_COLOR,
                            borderRadius: "2px",
                            pointerEvents: "none",
                          }}
                        />
                      )}
                      {showBottomLine && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: "-1px",
                            left: "8px",
                            right: "4px",
                            height: "2px",
                            backgroundColor: DROP_LINE_COLOR,
                            borderRadius: "2px",
                            pointerEvents: "none",
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {/* 인디케이터 슬롯 — 항상 같은 폭을 확보해 제목을 일정하게
                            들여쓰고(스트리밍 토글 시 제목 안 밀림), 슬롯 안 중앙 정렬로
                            좌우 여백을 맞춘다. */}
                        <Box
                          sx={{
                            width: "18px",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {isStreamingTask && <StreamingIndicator />}
                        </Box>
                        {/* 제목 + 경고 + chip — 제목 오른쪽에 붙여 흐르게 배치.
                            공간 부족 시 제목만 줄어들고(⚠/chip 은 flexShrink 0),
                            ★ 만 우측 고정 컬럼에 남아 제목 잘림을 최소화한다. */}
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Typography
                            level="body-xs"
                            sx={{
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                              fontWeight: isActive ? 600 : 400,
                              color: theme.vars.palette.Content.Strong,
                            }}
                          >
                            {task.title || "(제목 없음)"}
                          </Typography>
                          {/* 유형 무관(기본 활동지 포함) 필수 메타 누락/참조 깨짐 표시.
                              네이티브 title 은 브라우저 고정 딜레이가 있어 Joy Tooltip 사용. */}
                          {metaIssue && (
                            <Tooltip
                              title={metaIssue}
                              size="sm"
                              variant="solid"
                            >
                              <Typography
                                level="body-xs"
                                sx={{
                                  flexShrink: 0,
                                  ml: "4px",
                                  fontSize: "11px",
                                  color: theme.vars.palette.danger[500],
                                  lineHeight: 1,
                                }}
                              >
                                ⚠
                              </Typography>
                            </Tooltip>
                          )}
                          {task.task_type && task.task_type !== "basic" && (
                            <Typography
                              level="body-xs"
                              sx={{
                                flexShrink: 0,
                                ml: "4px",
                                fontSize: "10px",
                                lineHeight: 1,
                                px: "5px",
                                py: "3px",
                                borderRadius: "999px",
                                border: `1px solid ${theme.vars.palette.Border.Weak}`,
                                color: theme.vars.palette.Content.Medium,
                                whiteSpace: "nowrap",
                              }}
                              title={`${TASK_TYPE_LABELS[task.task_type]} 활동지`}
                            >
                              {TASK_TYPE_LABELS[task.task_type]}
                            </Typography>
                          )}
                        </Box>
                        {/* ★ 슬롯 — 고정 폭으로 행마다 같은 컬럼에 정렬 */}
                        <Box
                          sx={{
                            width: "14px",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {task.is_key_activity && (
                            <Typography
                              level="body-xs"
                              sx={{
                                fontSize: "11px",
                                color: KEY_BADGE_COLOR,
                                lineHeight: 1,
                              }}
                              title="Key 활동지"
                            >
                              ★
                            </Typography>
                          )}
                        </Box>
                        <Box
                          className="meta-edit-btn"
                          onClick={(event) => {
                            event.stopPropagation();
                            onEditMeta(task.task_id);
                          }}
                          sx={{
                            flexShrink: 0,
                            fontSize: "14px",
                            lineHeight: 1,
                            color: theme.vars.palette.Content.Weak,
                            cursor: "pointer",
                            px: "2px",
                            "&:hover": {
                              color: theme.vars.palette.Content.Strong,
                            },
                          }}
                          title="메타데이터 편집"
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{
                              fontSize: "16px",
                              // 아이콘 폰트(font-display: block) 로드 전엔 ligature
                              // 원문("more_vert")이 fallback 폭을 차지해 우측 배지가
                              // 왼쪽에서 점프한다 — 글리프 크기로 박스를 고정해 방지.
                              width: "16px",
                              height: "16px",
                              overflow: "hidden",
                              display: "inline-block",
                            }}
                          >
                            more_vert
                          </span>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          );
        })}
      </Box>

      {/* 하단 footer — 패널 접기 토글. 좌측 정렬: 접힘 시 같은 자리의
          floating 펼치기 버튼으로 이어져 토글 위치가 고정돼 보인다. */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "8px",
          py: "6px",
          borderTop: `1px solid ${theme.vars.palette.Border.Weak}`,
        }}
      >
        <CollapseToggleButton collapsed={false} onClick={onToggleCollapse} />
        <Box
          component="button"
          type="button"
          title="프로그램 버전 — 릴리즈 노트 보기"
          onClick={() => setVersionModalOpen(true)}
          sx={{
            // 헤더 "새 활동지" 텍스트의 오른쪽 라인(헤더 px 12 + sm 버튼
            // paddingInline 12 = 24px)에 텍스트를 맞춤 — footer px 8 + mr 16.
            mr: "16px",
            p: 0,
            border: "none",
            backgroundColor: "transparent",
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: "11px",
            lineHeight: 1,
            color: theme.vars.palette.Content.Weak,
            "&:hover": {
              color: theme.vars.palette.Content.Strong,
              textDecoration: "underline",
              textUnderlineOffset: "3px",
            },
          }}
        >
          {PROGRAM_VERSION}
        </Box>
      </Box>

      <ProgramVersionModal
        open={versionModalOpen}
        onClose={() => setVersionModalOpen(false)}
      />
    </Sheet>
  );
}
